/**
 * Worksheet Photo Gallery Component
 * SRV-FG-3: Fotó feltöltés és galéria nézet munkalapokhoz
 */

import {
  deleteWorksheetPhoto,
  getWorksheetPhotos,
  uploadWorksheetPhoto,
  type WorksheetPhoto,
  type WorksheetPhotoType,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

const PHOTO_TYPE_LABEL_KEYS: Record<WorksheetPhotoType, string> = {
  BEFORE: 'service.photoTypeBefore',
  AFTER: 'service.photoTypeAfter',
  DAMAGE: 'service.photoTypeDamage',
  GENERAL: 'service.photoTypeGeneral',
};

const PHOTO_TYPE_COLORS: Record<WorksheetPhotoType, string> = {
  BEFORE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  AFTER: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  DAMAGE: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  GENERAL: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

interface WorksheetPhotoGalleryProps {
  worksheetId: string;
  isEditable: boolean;
}

export function WorksheetPhotoGallery({ worksheetId, isEditable }: WorksheetPhotoGalleryProps) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<WorksheetPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [caption, setCaption] = useState('');
  const [photoType, setPhotoType] = useState<WorksheetPhotoType>('GENERAL');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorksheetPhotos(worksheetId);
      setPhotos(response.data);
    } catch {
      // Non-critical failure
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    void fetchPhotos();
  }, [fetchPhotos]);

  const handleUpload = async () => {
    const fileInput = fileInputRef.current;
    const file = fileInput?.files?.[0];
    if (!file) {
      setError(t('service.photoFileRequired'));
      return;
    }

    setUploading(true);
    setError(null);
    try {
      await uploadWorksheetPhoto(worksheetId, file, caption || undefined, photoType);
      setCaption('');
      setPhotoType('GENERAL');
      setShowUploadForm(false);
      if (fileInput) fileInput.value = '';
      await fetchPhotos();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: string) => {
    try {
      await deleteWorksheetPhoto(worksheetId, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch {
      // Silent failure for delete
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {t('service.photos')}
            {photos.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">({photos.length})</span>
            )}
          </CardTitle>
          {isEditable && (
            <Button variant="outline" size="sm" onClick={() => setShowUploadForm(!showUploadForm)}>
              {t('service.uploadPhoto')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Upload form */}
        {showUploadForm && (
          <div className="mb-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
            <h4 className="text-sm font-medium mb-3">{t('service.uploadPhoto')}</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  {t('service.photoFile')}
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="block w-full text-sm text-gray-500 dark:text-gray-400
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary/10 file:text-primary
                    hover:file:bg-primary/20"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('service.photoCaption')}
                  </label>
                  <Input
                    value={caption}
                    onChange={e => setCaption(e.target.value)}
                    placeholder={t('service.photoCaptionPlaceholder')}
                    maxLength={500}
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">
                    {t('service.photoTypeLabel')}
                  </label>
                  <select
                    value={photoType}
                    onChange={e => setPhotoType(e.target.value as WorksheetPhotoType)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  >
                    <option value="GENERAL">{t('service.photoTypeGeneral')}</option>
                    <option value="BEFORE">{t('service.photoTypeBefore')}</option>
                    <option value="AFTER">{t('service.photoTypeAfter')}</option>
                    <option value="DAMAGE">{t('service.photoTypeDamage')}</option>
                  </select>
                </div>
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleUpload} disabled={uploading}>
                  {uploading ? t('common.uploading') : t('common.upload')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowUploadForm(false);
                    setError(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Photo grid */}
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : photos.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('service.noPhotos')}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map(photo => (
              <div
                key={photo.id}
                className="group relative rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Photo thumbnail */}
                <button
                  type="button"
                  className="block w-full aspect-square bg-gray-100 dark:bg-gray-800 cursor-pointer"
                  onClick={() => setPreviewUrl(photo.url)}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption ?? photo.fileName}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>

                {/* Photo info overlay */}
                <div className="p-2">
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PHOTO_TYPE_COLORS[photo.photoType]}`}
                  >
                    {t(PHOTO_TYPE_LABEL_KEYS[photo.photoType])}
                  </span>
                  {photo.caption && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">{photo.caption}</p>
                  )}
                  <p className="text-xs text-muted-foreground">{formatFileSize(photo.fileSize)}</p>
                </div>

                {/* Delete button */}
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => handleDelete(photo.id)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title={t('common.delete')}
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Full-size preview modal */}
        {previewUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setPreviewUrl(null)}
            role="dialog"
          >
            <button
              type="button"
              className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/40"
              onClick={() => setPreviewUrl(null)}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
